package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * The kinds of placeholder Lingvo understands across every supported file format.
 *
 * This is the seam between file formats and machine translation: a translation engine that drops,
 * reorders or retypes a placeholder produces a string that crashes the customer's app on Apple
 * platforms (a wrong argument width corrupts every subsequent argument), so every value Lingvo
 * stores is validated against its source with [Placeholders.validate].
 */
@Serializable
enum class PlaceholderSyntax {
    /** `%%` — a literal percent sign, not an argument. */
    ESCAPED_PERCENT,

    /** `%s`, `%d`, `%@`, `%lld`, `%.2f` — bound to arguments **by position in the string**. */
    PRINTF,

    /** `%1${'$'}s`, `%2${'$'}@` — bound to arguments by explicit index, so they may be reordered freely. */
    POSITIONAL_PRINTF,

    /** `%#@count@` — an Apple `.stringsdict` variable that expands to a plural sub-dictionary. */
    APPLE_VARIABLE,

    /** `%(name)s` — Python / gettext named interpolation. */
    PYTHON_NAMED,

    /** `{count}`, `{user.name}` — ICU / Compose-style named argument. */
    ICU_NAMED,

    /** `{0}`, `{1}` — indexed argument. */
    ICU_INDEXED,

    /** `{{name}}` — Mustache / Handlebars. */
    MUSTACHE,

    /** `${'$'}name` — shell/template style. */
    DOLLAR_NAMED,

    /** `<xliff:g id="...">…</xliff:g>` — an Android do-not-translate span. */
    XLIFF_G,
}

/** One placeholder occurrence, with everything needed to compare it against another string. */
@Serializable
data class Placeholder(
    /** The exact substring as it appeared in the text. */
    val raw: String,
    val syntax: PlaceholderSyntax,
    /** 1-based argument index for [PlaceholderSyntax.POSITIONAL_PRINTF] and [PlaceholderSyntax.ICU_INDEXED]. */
    val index: Int? = null,
    /** Argument name for the named syntaxes. */
    val name: String? = null,
    /** printf conversion character (`s`, `d`, `@`, `f`, …), lower-cased. */
    val conversion: String? = null,
    /** printf length modifier (`ll`, `l`, `h`, …) — part of the argument's width on Apple platforms. */
    val lengthModifier: String? = null,
    /**
     * printf flag characters such as `-`, `+`, `0`.
     *
     * Deliberately excluded from [canonical] — a width or precision change is cosmetic — but not
     * ignored: some flags are illegal for some conversions and crash at format time. See
     * [Placeholders.illegalFlags].
     */
    val flags: String = "",
    val start: Int = 0,
    val end: Int = 0,
) {
    /**
     * A comparable form that ignores cosmetic differences (flags, width, precision) but keeps
     * everything that changes how an argument is bound or how wide it is.
     */
    val canonical: String
        get() = when (syntax) {
            PlaceholderSyntax.ESCAPED_PERCENT -> "%%"
            PlaceholderSyntax.PRINTF -> "%" + (lengthModifier ?: "") + (conversion ?: "")
            PlaceholderSyntax.POSITIONAL_PRINTF -> "%$index$" + (lengthModifier ?: "") + (conversion ?: "")
            PlaceholderSyntax.APPLE_VARIABLE -> "%#@$name@"
            PlaceholderSyntax.PYTHON_NAMED -> "%($name)${conversion ?: ""}"
            PlaceholderSyntax.ICU_NAMED -> "{$name}"
            PlaceholderSyntax.ICU_INDEXED -> "{$index}"
            PlaceholderSyntax.MUSTACHE -> "{{$name}}"
            PlaceholderSyntax.DOLLAR_NAMED -> "\$$name"
            PlaceholderSyntax.XLIFF_G -> "<xliff:g>"
        }

    /** True when this placeholder consumes an argument (so its position or index matters). */
    val isArgument: Boolean
        get() = syntax != PlaceholderSyntax.ESCAPED_PERCENT && syntax != PlaceholderSyntax.XLIFF_G
}

@Serializable
enum class PlaceholderProblemKind { MISSING, EXTRA, REORDERED, TYPE_CHANGED, ILLEGAL_FLAG }

@Serializable
data class PlaceholderProblem(
    val kind: PlaceholderProblemKind,
    val placeholder: String,
    val message: String,
)

/**
 * Extraction and validation of placeholders.
 *
 * Alternation order is load-bearing: `%%` is consumed first so that `100%%d` is a literal percent
 * followed by the letter `d` and not a `%d` argument, and the Apple `%#@var@` form is matched before
 * the generic printf form.
 */
object Placeholders {

    private const val FLAGS = "[-+ #0']*"
    private const val WIDTH = """(?:\d+|\*)?"""
    private const val PRECISION = """(?:\.(?:\d+|\*))?"""
    private const val LENGTH = """(?:hh|h|ll|l|q|L|z|t|j)?"""
    private const val CONV = "[diouxXeEfFgGaAcspn@]"

    private val regex = Regex(
        listOf(
            // %% — literal percent
            """(?<pct>%%)""",
            // %#@variable@ — Apple .stringsdict variable
            """(?<apple>%#@(?<appleName>[A-Za-z_][A-Za-z0-9_]*)@)""",
            // %(name)s — Python named
            """(?<py>%\((?<pyName>[A-Za-z_][A-Za-z0-9_]*)\)(?<pyConv>$CONV))""",
            // %1${'$'}s — positional printf
            """(?<pos>%(?<posIndex>\d+)\${'$'}(?<posFlags>$FLAGS)$WIDTH$PRECISION(?<posLen>$LENGTH)(?<posConv>$CONV))""",
            // %s / %lld / %.2f — plain printf
            """(?<printf>%(?<pfFlags>$FLAGS)$WIDTH$PRECISION(?<pfLen>$LENGTH)(?<pfConv>$CONV))""",
            // <xliff:g …>…</xliff:g>
            """(?<xliff><xliff:g\b[^>]*>[\s\S]*?</xliff:g>)""",
            // {{name}} — mustache
            """(?<mustache>\{\{(?<mustacheName>[A-Za-z_][A-Za-z0-9_.-]*)\}\})""",
            // {0} — indexed
            """(?<icuIdx>\{(?<icuIndex>\d+)\})""",
            // {name} — named
            """(?<icu>\{(?<icuName>[A-Za-z_][A-Za-z0-9_.-]*)\})""",
            // ${'$'}name
            """(?<dollar>\${'$'}(?<dollarName>[A-Za-z_][A-Za-z0-9_]*))""",
        ).joinToString("|"),
    )

    private fun MatchResult.group(name: String): String? =
        runCatching { groups[name]?.value }.getOrNull()

    /** Every placeholder in [text], in the order they appear. */
    fun extractAll(text: String?): List<Placeholder> {
        if (text.isNullOrEmpty()) return emptyList()
        return regex.findAll(text).map { m ->
            val range = m.range
            when {
                m.group("pct") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.ESCAPED_PERCENT,
                    start = range.first, end = range.last + 1,
                )

                m.group("apple") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.APPLE_VARIABLE,
                    name = m.group("appleName"), start = range.first, end = range.last + 1,
                )

                m.group("py") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.PYTHON_NAMED,
                    name = m.group("pyName"), conversion = m.group("pyConv"),
                    start = range.first, end = range.last + 1,
                )

                m.group("pos") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.POSITIONAL_PRINTF,
                    index = m.group("posIndex")?.toIntOrNull(),
                    conversion = m.group("posConv"),
                    lengthModifier = m.group("posLen")?.takeIf { it.isNotEmpty() },
                    flags = m.group("posFlags").orEmpty(),
                    start = range.first, end = range.last + 1,
                )

                m.group("printf") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.PRINTF,
                    conversion = m.group("pfConv"),
                    lengthModifier = m.group("pfLen")?.takeIf { it.isNotEmpty() },
                    flags = m.group("pfFlags").orEmpty(),
                    start = range.first, end = range.last + 1,
                )

                m.group("xliff") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.XLIFF_G,
                    start = range.first, end = range.last + 1,
                )

                m.group("mustache") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.MUSTACHE,
                    name = m.group("mustacheName"), start = range.first, end = range.last + 1,
                )

                m.group("icuIdx") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.ICU_INDEXED,
                    index = m.group("icuIndex")?.toIntOrNull(), start = range.first, end = range.last + 1,
                )

                m.group("icu") != null -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.ICU_NAMED,
                    name = m.group("icuName"), start = range.first, end = range.last + 1,
                )

                else -> Placeholder(
                    raw = m.value, syntax = PlaceholderSyntax.DOLLAR_NAMED,
                    name = m.group("dollarName"), start = range.first, end = range.last + 1,
                )
            }
        }.toList()
    }

    /** The raw placeholder strings, in order. Kept for callers that only need to highlight them. */
    fun extract(text: String?): List<String> =
        extractAll(text).filter { it.isArgument }.map { it.raw }

    /** Canonical comparable tokens, in order, excluding literal `%%` and xliff spans. */
    fun signature(text: String?): List<String> =
        extractAll(text).filter { it.isArgument }.map { it.canonical }

    /**
     * Compare a translated value against its source.
     *
     * Rules, in the order they fire:
     * 1. every source placeholder must appear in the target the same number of times, and vice versa;
     * 2. **unindexed** printf placeholders must appear in the *same order* — they bind to arguments
     *    positionally, so reordering them silently swaps the arguments (on Apple this also corrupts
     *    argument widths and crashes);
     * 3. indexed placeholders (`%1${'$'}s`, `{0}`) and named placeholders may be reordered freely, since
     *    the binding travels with the token.
     *
     * Returns an empty list when the target is safe to store.
     */
    /**
     * Flags that are illegal for a given conversion and throw at format time.
     *
     * Verified against the JVM: `%+s`, `% s`, `%#s` and `%05s` all raise
     * `FormatFlagsConversionMismatchException`, while width and precision changes such as
     * `%.2f` -> `%f` are merely cosmetic. That is why [Placeholder.canonical] ignores width and
     * precision but flags are checked separately — a translator or a machine adding `+` or `0` to a
     * `%s` produces a string that compiles, imports, exports, and crashes the app on display.
     */
    private const val NON_NUMERIC_CONVERSIONS = "sScCbB@"
    private const val NUMERIC_ONLY_FLAGS = "+ #0"

    internal fun illegalFlags(placeholder: Placeholder): String {
        val conversion = placeholder.conversion?.takeIf { it.length == 1 } ?: return ""
        if (conversion[0] !in NON_NUMERIC_CONVERSIONS) return ""
        return placeholder.flags.filter { flag -> flag in NUMERIC_ONLY_FLAGS }
    }

    fun validate(source: String?, target: String?): List<PlaceholderProblem> {
        val sourceArgs = extractAll(source).filter { it.isArgument }
        val targetArgs = extractAll(target).filter { it.isArgument }
        if (sourceArgs.isEmpty() && targetArgs.isEmpty()) return emptyList()

        val problems = mutableListOf<PlaceholderProblem>()

        // A flag the source did not have, on a conversion that cannot take it. Only reported when
        // the source is clean, so an already-broken source string produces one problem, not two.
        val sourceRaw = sourceArgs.mapTo(mutableSetOf()) { it.raw }
        targetArgs.forEach { placeholder ->
            val illegal = illegalFlags(placeholder)
            if (illegal.isNotEmpty() && placeholder.raw !in sourceRaw) {
                problems += PlaceholderProblem(
                    kind = PlaceholderProblemKind.ILLEGAL_FLAG,
                    placeholder = placeholder.raw,
                    message = "${placeholder.raw} is not a valid format string — the flag " +
                        "'${illegal.first()}' cannot be used with %${placeholder.conversion}, " +
                        "and formatting it crashes at runtime.",
                )
            }
        }

        val sourceCounts = sourceArgs.groupingBy { it.canonical }.eachCount()
        val targetCounts = targetArgs.groupingBy { it.canonical }.eachCount()

        for ((token, count) in sourceCounts) {
            val found = targetCounts[token] ?: 0
            if (found < count) {
                problems += PlaceholderProblem(
                    kind = PlaceholderProblemKind.MISSING,
                    placeholder = token,
                    message = if (found == 0) {
                        "Missing $token — the source uses it but the translation does not."
                    } else {
                        "$token appears $found time(s) but the source uses it $count time(s)."
                    },
                )
            }
        }
        for ((token, count) in targetCounts) {
            val expected = sourceCounts[token] ?: 0
            if (count > expected) {
                problems += PlaceholderProblem(
                    kind = if (expected == 0) PlaceholderProblemKind.EXTRA else PlaceholderProblemKind.EXTRA,
                    placeholder = token,
                    message = if (expected == 0) {
                        "$token is not in the source string."
                    } else {
                        "$token appears $count time(s) but the source only uses it $expected time(s)."
                    },
                )
            }
        }

        // A same-token-set change of conversion (e.g. %d -> %s) shows up above as MISSING+EXTRA;
        // report it once, more usefully, when the counts otherwise line up.
        if (problems.size == 2 && sourceArgs.size == targetArgs.size) {
            val missing = problems.firstOrNull { it.kind == PlaceholderProblemKind.MISSING }
            val extra = problems.firstOrNull { it.kind == PlaceholderProblemKind.EXTRA }
            if (missing != null && extra != null) {
                return listOf(
                    PlaceholderProblem(
                        kind = PlaceholderProblemKind.TYPE_CHANGED,
                        placeholder = missing.placeholder,
                        message = "${missing.placeholder} became ${extra.placeholder} — the argument type must not change.",
                    ),
                )
            }
        }

        if (problems.isNotEmpty()) return problems

        // Counts match. Unindexed printf placeholders must keep their relative order.
        val sourceOrder = sourceArgs.filter { it.syntax == PlaceholderSyntax.PRINTF }.map { it.canonical }
        val targetOrder = targetArgs.filter { it.syntax == PlaceholderSyntax.PRINTF }.map { it.canonical }
        if (sourceOrder != targetOrder && sourceOrder.toSet() == targetOrder.toSet()) {
            problems += PlaceholderProblem(
                kind = PlaceholderProblemKind.REORDERED,
                placeholder = sourceOrder.joinToString(" "),
                message = "Placeholders were reordered (${sourceOrder.joinToString(" ")} became " +
                    "${targetOrder.joinToString(" ")}). Unindexed placeholders bind to arguments by " +
                    "position — use indexed forms like %1\$s if the translation needs a different order.",
            )
        }

        return problems
    }

    /** Convenience: is this translation safe to store? */
    fun isValid(source: String?, target: String?): Boolean = validate(source, target).isEmpty()
}
